/**
 * Custom Main Product Section JavaScript
 * Handles media gallery, variant selection, and product form interactions
 * Full Dawn compatibility: uses publish/subscribe, fetchConfig, renderContents
 */

/* ==========================================================================
   CUSTOM MEDIA GALLERY COMPONENT
   Handles thumbnail navigation, media switching, and lightbox
   ========================================================================== */

class CustomMediaGallery extends HTMLElement {
    constructor() {
        super();
        this.currentIndex = 0;
        this.mediaItems = [];
        this.thumbnails = [];
    }

    connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mediaItems = Array.from(this.querySelectorAll('[data-media-item]'));
        this.thumbnails = Array.from(this.querySelectorAll('[data-thumbnail]'));
        this.statusElement = document.getElementById(`CustomGalleryStatus-${this.sectionId}`);

        this.prevButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.currentSlideIndicator = this.querySelector('[data-current-slide]');

        this.bindEvents();
        this.initializeMedia();
    }

    bindEvents() {
        // Thumbnail click events
        this.thumbnails.forEach((thumbnail, index) => {
            thumbnail.addEventListener('click', () => this.goToSlide(index));
        });

        // Navigation buttons
        if (this.prevButton) {
            this.prevButton.addEventListener('click', () => this.previousSlide());
        }
        if (this.nextButton) {
            this.nextButton.addEventListener('click', () => this.nextSlide());
        }

        // Keyboard navigation
        this.addEventListener('keydown', (e) => this.handleKeydown(e));

        // Variant change listener (custom event)
        document.addEventListener('variant:changed', (e) => {
            if (e.detail.sectionId === this.sectionId) {
                this.handleVariantChange(e.detail);
            }
        });
    }

    initializeMedia() {
        const activeMedia = this.querySelector('[data-media-item].is-active');
        if (activeMedia) {
            const mediaId = activeMedia.dataset.mediaId;
            const thumbnail = this.querySelector(`[data-thumbnail][data-media-id="${mediaId}"]`);
            if (thumbnail) {
                this.currentIndex = parseInt(thumbnail.dataset.index, 10) || 0;
            }
        }
    }

    goToSlide(index) {
        if (index < 0 || index >= this.mediaItems.length) return;

        const targetThumbnail = this.thumbnails[index];
        if (!targetThumbnail) return;

        const mediaId = targetThumbnail.dataset.mediaId;
        this.switchMedia(mediaId, index);
    }

    switchMedia(mediaId, index) {
        // Pause any playing media
        this.pauseAllMedia();

        // Update media items
        this.mediaItems.forEach((item) => {
            item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
        });

        // Update thumbnails
        this.thumbnails.forEach((thumb) => {
            thumb.setAttribute('aria-current', thumb.dataset.mediaId === mediaId ? 'true' : 'false');
        });

        this.currentIndex = index;

        // Update slide counter
        if (this.currentSlideIndicator) {
            this.currentSlideIndicator.textContent = index + 1;
        }

        this.updateStatus();
        this.scrollThumbnailIntoView(index);
    }

    /**
     * Set active media from external callers (e.g. variant change)
     * @param {string} mediaId - The media ID to activate
     */
    setActiveMedia(mediaId) {
        const thumbnail = this.querySelector(`[data-thumbnail][data-media-id="${mediaId}"]`);
        if (thumbnail) {
            const index = parseInt(thumbnail.dataset.index, 10) || 0;
            this.switchMedia(mediaId, index);
        }
    }

    previousSlide() {
        const newIndex = this.currentIndex === 0
            ? this.mediaItems.length - 1
            : this.currentIndex - 1;
        this.goToSlide(newIndex);
    }

    nextSlide() {
        const newIndex = this.currentIndex === this.mediaItems.length - 1
            ? 0
            : this.currentIndex + 1;
        this.goToSlide(newIndex);
    }

    handleKeydown(e) {
        if (e.key === 'ArrowLeft') {
            this.previousSlide();
        } else if (e.key === 'ArrowRight') {
            this.nextSlide();
        }
    }

    handleVariantChange(detail) {
        const { variant } = detail;
        if (!variant || !variant.featured_media) return;

        const mediaId = variant.featured_media.id.toString();
        this.setActiveMedia(mediaId);
    }

    pauseAllMedia() {
        this.querySelectorAll('video').forEach((video) => video.pause());
        this.querySelectorAll('model-viewer').forEach((model) => {
            if (model.pause) model.pause();
        });
        this.querySelectorAll('.deferred-media').forEach((media) => {
            media.removeAttribute('loaded');
            const content = media.querySelector('video, model-viewer, iframe');
            if (content) content.remove();
        });
    }

    scrollThumbnailIntoView(index) {
        const thumbnail = this.thumbnails[index];
        if (!thumbnail) return;

        thumbnail.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
        });
    }

    updateStatus() {
        if (!this.statusElement) return;

        const currentMedia = this.mediaItems[this.currentIndex];
        if (!currentMedia) return;

        const mediaType = currentMedia.dataset.mediaType;
        const position = this.currentIndex + 1;
        const total = this.mediaItems.length;

        let message = `Item ${position} of ${total}`;
        if (mediaType === 'video' || mediaType === 'external_video') {
            message += ', video';
        } else if (mediaType === 'model') {
            message += ', 3D model';
        }

        this.statusElement.textContent = message;
    }
}

if (!customElements.get('custom-media-gallery')) {
    customElements.define('custom-media-gallery', CustomMediaGallery);
}


/* ==========================================================================
   VARIANT SELECTOR COMPONENT
   Handles variant option selection and updates
   ========================================================================== */

class CustomVariantSelector extends HTMLElement {
    constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.productUrl;
        this.product = null;
    }

    connectedCallback() {
        this.options = Array.from(this.querySelectorAll('[data-variant-option]'));
        this.variantIdInput = document.querySelector(`#custom-pdp-variant-id-${this.sectionId}`);

        this.loadProductData();

        this.options.forEach((option) => {
            option.addEventListener('change', this.onOptionChange.bind(this));
        });
    }

    async loadProductData() {
        try {
            const response = await fetch(`${this.productUrl}.js`);
            this.product = await response.json();
        } catch (error) {
            console.error('Error loading product data:', error);
        }
    }

    onOptionChange() {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.findVariant(selectedOptions);

        if (variant) {
            this.updateVariantInput(variant);
            this.updatePrice(variant);
            this.updateSku(variant);
            this.updateAvailability(variant);
            this.dispatchVariantChange(variant);
        }
    }

    getSelectedOptions() {
        const options = [];

        this.querySelectorAll('fieldset').forEach((fieldset) => {
            const checked = fieldset.querySelector('input:checked');
            const selected = fieldset.querySelector('select');

            if (checked) {
                options.push(checked.value);
            } else if (selected) {
                options.push(selected.value);
            }
        });

        return options;
    }

    findVariant(selectedOptions) {
        if (!this.product || !this.product.variants) return null;

        return this.product.variants.find((variant) => {
            return variant.options.every((option, index) => {
                return option === selectedOptions[index];
            });
        });
    }

    updateVariantInput(variant) {
        if (this.variantIdInput) {
            this.variantIdInput.value = variant.id;
            this.variantIdInput.disabled = !variant.available;
            this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update URL
        if (history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url);
        }
    }

    updatePrice(variant) {
        const priceElement = document.querySelector(`#custom-pdp-price-${this.sectionId}`);
        if (!priceElement) return;

        const formatter = new Intl.NumberFormat(Shopify.locale, {
            style: 'currency',
            currency: Shopify.currency.active
        });

        const priceValue = priceElement.querySelector('.custom-pdp__price--sale') ||
            priceElement.querySelector('.custom-pdp__price');
        const comparePrice = priceElement.querySelector('.custom-pdp__price--compare');

        if (priceValue) {
            priceValue.textContent = formatter.format(variant.price / 100);
        }

        if (comparePrice) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                comparePrice.textContent = formatter.format(variant.compare_at_price / 100);
                comparePrice.hidden = false;
            } else {
                comparePrice.hidden = true;
            }
        }
    }

    updateSku(variant) {
        const skuElement = document.querySelector(`#custom-pdp-sku-${this.sectionId}`);
        if (!skuElement) return;

        const skuValue = skuElement.querySelector('.custom-pdp__sku-value');
        if (skuValue && variant.sku) {
            skuValue.textContent = variant.sku;
            skuElement.hidden = false;
        } else if (!variant.sku) {
            skuElement.hidden = true;
        }
    }

    updateAvailability(variant) {
        const addButton = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const errorWrapper = document.querySelector('.product-form__error-message-wrapper');

        if (addButton) {
            addButton.disabled = !variant.available;

            const buttonText = addButton.querySelector('span:not(.loading__spinner)');
            if (buttonText) {
                buttonText.textContent = variant.available
                    ? (addButton.dataset.addText || 'Add to Cart')
                    : (addButton.dataset.soldoutText || 'Sold Out');
            }
        }

        // Hide error message on variant change
        if (errorWrapper) {
            errorWrapper.hidden = true;
        }
    }

    dispatchVariantChange(variant) {
        // Dispatch custom event for gallery
        document.dispatchEvent(new CustomEvent('variant:changed', {
            detail: {
                sectionId: this.sectionId,
                variant: variant
            }
        }));

        // Also publish via Dawn's PubSub system if available
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.variantChange, {
                data: {
                    sectionId: this.sectionId,
                    variant: variant,
                }
            });
        }
    }
}

if (!customElements.get('custom-variant-selector')) {
    customElements.define('custom-variant-selector', CustomVariantSelector);
}


/* ==========================================================================
   QUANTITY INPUT COMPONENT
   Handles quantity increment/decrement
   ========================================================================== */

class CustomQuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input[type="number"]');
        this.buttons = this.querySelectorAll('button');
    }

    connectedCallback() {
        this.buttons.forEach((button) => {
            button.addEventListener('click', () => {
                const delta = button.name === 'plus' ? 1 : -1;
                this.changeQuantity(delta);
            });
        });
        this.input?.addEventListener('change', () => this.validateQuantity());
    }

    changeQuantity(delta) {
        const currentValue = parseInt(this.input.value, 10) || 1;
        const min = parseInt(this.input.min, 10) || 1;
        const max = parseInt(this.input.max, 10) || Infinity;

        const newValue = Math.min(Math.max(currentValue + delta, min), max);
        this.input.value = newValue;

        this.updateButtons(newValue, min, max);
    }

    validateQuantity() {
        const min = parseInt(this.input.min, 10) || 1;
        const max = parseInt(this.input.max, 10) || Infinity;
        let value = parseInt(this.input.value, 10) || min;

        value = Math.min(Math.max(value, min), max);
        this.input.value = value;

        this.updateButtons(value, min, max);
    }

    updateButtons(value, min, max) {
        this.buttons.forEach((button) => {
            if (button.name === 'minus') {
                button.disabled = value <= min;
            } else if (button.name === 'plus') {
                button.disabled = value >= max;
            }
        });
    }
}

// Use Dawn's quantity-input if not already registered
if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', CustomQuantityInput);
}


/* ==========================================================================
   DEFERRED MEDIA COMPONENT
   Handles lazy loading of videos and 3D models
   ========================================================================== */

class CustomDeferredMedia extends HTMLElement {
    constructor() {
        super();
        this.posterButton = this.querySelector('.deferred-media__poster');
    }

    connectedCallback() {
        this.posterButton?.addEventListener('click', this.loadMedia.bind(this));
    }

    loadMedia() {
        if (this.hasAttribute('loaded')) return;

        const template = this.querySelector('template');
        if (!template) return;

        const content = template.content.cloneNode(true);
        this.appendChild(content);
        this.setAttribute('loaded', '');

        if (this.posterButton) {
            this.posterButton.hidden = true;
        }

        const media = this.querySelector('video, model-viewer, iframe');
        if (media) media.focus();
    }
}

if (!customElements.get('deferred-media')) {
    customElements.define('deferred-media', CustomDeferredMedia);
}


/* ==========================================================================
   PRODUCT MODAL (Lightbox) COMPONENT
   ========================================================================== */

class CustomProductModal extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        // Query children in connectedCallback (DOM is guaranteed ready)
        this.dialog = this.querySelector('dialog');
        this.closeButton = this.querySelector('[data-modal-close]');
        this.imageContainer = this.querySelector('[data-modal-content]');

        this.closeButton?.addEventListener('click', () => this.hide());

        // Close when clicking the dialog backdrop area
        this.dialog?.addEventListener('click', (e) => {
            if (e.target === this.dialog) this.hide();
        });

        // Close on escape
        this.dialog?.addEventListener('cancel', (e) => {
            e.preventDefault(); // Prevent native close so we control cleanup
            this.hide();
        });
    }

    /**
     * show(opener) — Dawn's ModalOpener calls modal.show(button).
     * This is the primary entry point for opening the lightbox.
     */
    show(opener) {
        const mediaId = opener?.dataset?.mediaId;

        if (mediaId && this.imageContainer) {
            const gallery = document.querySelector(`custom-media-gallery[data-section-id="${this.dataset.sectionId}"]`);
            if (gallery) {
                const mediaItem = gallery.querySelector(`[data-media-item][data-media-id="${mediaId}"]`);
                if (mediaItem) {
                    const image = mediaItem.querySelector('img');
                    if (image) {
                        const modalImage = this.imageContainer.querySelector('img') || document.createElement('img');
                        modalImage.src = image.src.replace(/width=\d+/, 'width=2000');
                        modalImage.alt = image.alt;
                        modalImage.className = 'custom-pdp-modal__image';

                        if (!this.imageContainer.contains(modalImage)) {
                            this.imageContainer.appendChild(modalImage);
                        }
                    }
                }
            }
        }

        this.openedBy = opener;
        this.setAttribute('open', '');
        this.dialog?.showModal();
        document.body.classList.add('overflow-hidden');
    }

    /**
     * hide() — matches Dawn's ModalDialog.hide() convention.
     */
    hide() {
        this.removeAttribute('open');
        this.dialog?.close();
        document.body.classList.remove('overflow-hidden');
    }

    /* Aliases so both Dawn convention (.show/.hide) and generic (.open/.close) work */
    open(opener) { this.show(opener); }
    close() { this.hide(); }
}

if (!customElements.get('custom-product-modal')) {
    customElements.define('custom-product-modal', CustomProductModal);
}

/*
 * NOTE: We intentionally do NOT re-register 'modal-opener'.
 * Dawn's global.js already registers ModalOpener which calls modal.show(button).
 * Our CustomProductModal.show() method is compatible with that interface.
 */


/* ==========================================================================
   PRODUCT FORM - DAWN COMPATIBLE
   Override the built-in product-form element to work with our custom markup
   Uses Dawn's publish/subscribe system, fetchConfig, and cart renderContents
   ========================================================================== */

/**
 * We re-register product-form ONLY if Dawn hasn't already registered it.
 * Dawn's product-form.js registers it with the same name. Since we load
 * custom-main-product.js with defer, Dawn's global.js and product-form.js
 * will already have registered it. Our Liquid uses <product-form> tag,
 * so Dawn's product-form class will handle the submit. No need to override.
 *
 * However, we need to ensure the submit button selector and spinner work
 * correctly with our custom markup. Dawn's product-form looks for:
 *   - this.form (the <form> element)
 *   - this.submitButton (querySelector('[type="submit"]'))
 *   - this.submitButtonText (submitButton.querySelector('span'))
 *   - this.querySelector('.loading__spinner')
 *   - this.cart (cart-notification or cart-drawer)
 *   - error wrapper: .product-form__error-message-wrapper
 *
 * Our Liquid output uses these exact selectors for compatibility.
 *
 * If Dawn's product-form is NOT registered (e.g. product-form.js not loaded),
 * we define a compatible fallback here:
 */

if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class CustomProductFormFallback extends HTMLElement {
            constructor() {
                super();
                this.form = this.querySelector('form');
                this.submitButton = this.querySelector('[type="submit"]');
                this.submitButtonText = this.submitButton?.querySelector('span');
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                this.hideErrors = this.dataset.hideErrors === 'true';

                if (this.form) {
                    const variantInput = this.form.querySelector('[name=id]');
                    if (variantInput) variantInput.disabled = false;
                    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
                }

                if (document.querySelector('cart-drawer') && this.submitButton) {
                    this.submitButton.setAttribute('aria-haspopup', 'dialog');
                }
            }

            onSubmitHandler(evt) {
                evt.preventDefault();
                if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

                this.handleErrorMessage();

                this.submitButton?.setAttribute('aria-disabled', true);
                this.submitButton?.classList.add('loading');

                const spinner = this.querySelector('.loading__spinner');
                spinner?.classList.remove('hidden');

                const config = typeof fetchConfig === 'function'
                    ? fetchConfig('javascript')
                    : { method: 'POST', headers: { Accept: 'application/javascript' } };

                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);
                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    if (typeof this.cart.setActiveElement === 'function') {
                        this.cart.setActiveElement(document.activeElement);
                    }
                }
                config.body = formData;

                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            // Error response
                            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                                publish(PUB_SUB_EVENTS.cartError, {
                                    source: 'product-form',
                                    productVariantId: formData.get('id'),
                                    errors: response.errors || response.description,
                                    message: response.message,
                                });
                            }
                            this.handleErrorMessage(response.description);
                            this.error = true;
                            return;
                        } else if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        if (!this.error) {
                            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                                publish(PUB_SUB_EVENTS.cartUpdate, {
                                    source: 'product-form',
                                    productVariantId: formData.get('id'),
                                    cartData: response,
                                });
                            }
                        }
                        this.error = false;

                        // Render updated cart sections (Dawn pattern)
                        if (this.cart && typeof this.cart.renderContents === 'function') {
                            this.cart.renderContents(response);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        this.submitButton?.classList.remove('loading');
                        if (this.cart?.classList.contains('is-empty')) {
                            this.cart.classList.remove('is-empty');
                        }
                        if (!this.error) {
                            this.submitButton?.removeAttribute('aria-disabled');
                        }
                        spinner?.classList.add('hidden');
                    });
            }

            handleErrorMessage(errorMessage = false) {
                if (this.hideErrors) return;

                this.errorMessageWrapper =
                    this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
                if (!this.errorMessageWrapper) return;

                this.errorMessage =
                    this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

                if (errorMessage && this.errorMessage) {
                    this.errorMessage.textContent = errorMessage;
                }
            }

            toggleSubmitButton(disable = true, text) {
                if (!this.submitButton) return;

                if (disable) {
                    this.submitButton.setAttribute('disabled', 'disabled');
                    if (text && this.submitButtonText) this.submitButtonText.textContent = text;
                } else {
                    this.submitButton.removeAttribute('disabled');
                    if (this.submitButtonText && window.variantStrings) {
                        this.submitButtonText.textContent = window.variantStrings.addToCart;
                    }
                }
            }

            get variantIdInput() {
                return this.form?.querySelector('[name=id]');
            }
        }
    );
}
